<template>
  <header class="site-header" :class="{ 'is-scrolled': isScrolled, 'menu-open': isMenuOpen }">
    <div class="header-inner">
      <router-link to="/" class="brand" @mouseenter="replayLogo">
        <svg class="monogram" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path class="j-letter" fill="none" d="M30,20 L30,80 M20,80 L40,80" />
          <path class="b-letter" fill="none" d="M60,20 L60,80 C75,80 80,65 80,50 C80,35 75,20 60,20 M60,50 C75,50 80,50 80,50" />
          <circle class="dot" cx="50" cy="50" r="5" />
        </svg>
        <span class="brand-text">Jerome Brown</span>
      </router-link>

      <nav ref="trackRef" class="desktop-nav" aria-label="Main">
        <component
          v-for="link in links"
          :key="link.name"
          :is="link.href ? 'a' : 'router-link'"
          v-bind="linkAttrs(link)"
          :ref="(el) => setLinkRef(link.name, el)"
          class="nav-link"
          :class="{ active: isActive(link) }"
          @mouseenter="previewBeam(link.name)"
          @mouseleave="hidePreview"
          @click="!link.href && closeMenu()"
        >
          {{ link.name }}<span v-if="link.beta" class="beta-tag">Beta</span>
        </component>

        <span ref="beamRef" class="beam beam-active" aria-hidden="true"></span>
        <span ref="beamPreviewRef" class="beam beam-preview" aria-hidden="true"></span>
      </nav>

      <button
        type="button"
        class="menu-toggle"
        :aria-expanded="isMenuOpen"
        aria-label="Toggle menu"
        @click="toggleMenu"
      >
        <span class="menu-bar" :class="{ open: isMenuOpen }"></span>
      </button>
    </div>

    <Teleport to="body">
      <transition name="overlay">
        <div v-if="isMenuOpen" class="mobile-overlay" @click.self="closeMenu">
          <nav class="mobile-nav" aria-label="Mobile">
            <component
              v-for="link in links"
              :key="link.name"
              :is="link.href ? 'a' : 'router-link'"
              v-bind="linkAttrs(link)"
              class="mobile-link"
              :class="{ active: isActive(link) }"
              @click="closeMenu"
            >
              <span class="mobile-link-text">{{ link.name }}<span v-if="link.beta" class="beta-tag">Beta</span></span>
              <span v-if="isActive(link)" class="mobile-link-tag">You are here</span>
            </component>
          </nav>
        </div>
      </transition>
    </Teleport>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { gsap } from 'gsap';
import { useRoute } from 'vue-router';

const route = useRoute();
const isMenuOpen = ref(false);
const isScrolled = ref(false);

const trackRef = ref(null);
const beamRef = ref(null);
const beamPreviewRef = ref(null);
const linkRefs = ref({});

const links = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
  { name: 'Blog', href: 'https://jeromejbrown.substack.com/' },
];

const setLinkRef = (name, el) => {
  if (el) linkRefs.value[name] = el.$el ?? el;
};

/**
 * External links render as a plain <a>; internal links render as
 * <router-link>. Binding an explicit `href` (even `undefined`) on
 * <router-link> clobbers the href it computes internally from `to`,
 * so each variant only ever receives the attributes it actually needs.
 */
const linkAttrs = (link) =>
  link.href
    ? { href: link.href, target: '_blank', rel: 'noopener noreferrer' }
    : { to: link.path };

const isActive = (link) => {
  if (!link.path) return false;
  if (link.path === '/') return route.path === '/';
  return route.path.startsWith(link.path);
};

const activeLinkName = () => links.find((l) => isActive(l))?.name ?? null;

const beamMetrics = (linkName) => {
  const el = linkRefs.value[linkName];
  const track = trackRef.value;
  if (!el || !track) return null;

  const t = track.getBoundingClientRect();
  const l = el.getBoundingClientRect();
  const pad = 6;

  return {
    x: l.left - t.left - pad,
    width: l.width + pad * 2,
  };
};

const moveBeam = (beamEl, linkName, { duration = 0.55, ease = 'power4.out', opacity = 1 } = {}) => {
  const m = beamMetrics(linkName);
  if (!beamEl || !m) return;

  gsap.to(beamEl, {
    x: m.x,
    width: m.width,
    opacity,
    duration,
    ease,
  });
};

const snapActiveBeam = (animate = true) => {
  const name = activeLinkName();
  if (!name) {
    gsap.to(beamRef.value, { opacity: 0, duration: 0.2 });
    return;
  }

  moveBeam(beamRef.value, name, {
    duration: animate ? 0.65 : 0,
    ease: animate ? 'expo.out' : 'none',
    opacity: 1,
  });
};

const previewBeam = (linkName) => {
  const link = links.find((l) => l.name === linkName);
  if (!link || isActive(link)) return;
  moveBeam(beamPreviewRef.value, linkName, { duration: 0.3, ease: 'power2.out', opacity: 0.45 });
};

const hidePreview = () => {
  gsap.to(beamPreviewRef.value, { opacity: 0, duration: 0.2 });
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  document.body.style.overflow = isMenuOpen.value ? 'hidden' : '';
};

const closeMenu = () => {
  isMenuOpen.value = false;
  document.body.style.overflow = '';
};

const replayLogo = () => {
  gsap.fromTo('.site-header .j-letter', { strokeDashoffset: 100 }, { strokeDashoffset: 0, duration: 0.5 });
  gsap.fromTo('.site-header .b-letter', { strokeDashoffset: 200 }, { strokeDashoffset: 0, duration: 0.65, delay: 0.08 });
};

const onScroll = () => {
  isScrolled.value = window.scrollY > 16;
};

const onResize = () => nextTick(() => snapActiveBeam(false));

const onKeydown = (e) => {
  if (e.key === 'Escape' && isMenuOpen.value) closeMenu();
};

watch(
  () => route.path,
  () => {
    closeMenu();
    nextTick(() => snapActiveBeam(true));
  }
);

onMounted(() => {
  gsap.to('.site-header .j-letter', { strokeDashoffset: 0, duration: 1, delay: 0.15 });
  gsap.to('.site-header .b-letter', { strokeDashoffset: 0, duration: 1.2, delay: 0.35 });

  nextTick(() => {
    gsap.set(beamPreviewRef.value, { opacity: 0 });
    snapActiveBeam(false);
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('keydown', onKeydown);
  onScroll();
});

onUnmounted(() => {
  document.body.style.overflow = '';
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onResize);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s ease, background 0.3s ease;
}

.site-header.is-scrolled {
  background: rgba(9, 12, 20, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.header-inner {
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1.25rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

/* Brand */
.brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  flex-shrink: 0;
}

.monogram {
  width: 2.25rem;
  height: 2.25rem;
}

.j-letter,
.b-letter {
  stroke-linecap: round;
  stroke-width: 8;
}

.j-letter {
  stroke: #3b82f6;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
}

.b-letter {
  stroke: #e5e7eb;
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
}

.dot {
  fill: #10b981;
}

.brand-text {
  font-size: 1rem;
  font-weight: 600;
  color: #f3f4f6;
  letter-spacing: -0.01em;
  transition: color 0.2s ease;
}

.brand:hover .brand-text {
  color: #99f6e4;
}

/* Desktop links */
.desktop-nav {
  position: relative;
  display: none;
  align-items: center;
  gap: 2rem;
  padding-bottom: 3px;
}

@media (min-width: 768px) {
  .desktop-nav {
    display: flex;
  }
}

.nav-link {
  position: relative;
  z-index: 1;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.25s ease;
  white-space: nowrap;
}

.nav-link:hover {
  color: #e5e7eb;
}

.nav-link.active {
  color: #ffffff;
}

/* The beam — one signature effect */
.beam {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 0;
  border-radius: 9999px;
  pointer-events: none;
  will-change: transform, width;
}

.beam-active {
  background: linear-gradient(90deg, #2dd4bf, #60a5fa);
  box-shadow:
    0 0 12px rgba(45, 212, 191, 0.65),
    0 0 24px rgba(59, 130, 246, 0.25);
}

.beam-preview {
  background: linear-gradient(90deg, rgba(45, 212, 191, 0.5), rgba(96, 165, 250, 0.35));
  box-shadow: none;
}

/* Mobile toggle — minimal morphing bars */
.menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  color: #d1d5db;
  background: none;
  border: none;
  cursor: pointer;
}

@media (min-width: 768px) {
  .menu-toggle {
    display: none;
  }
}

.menu-bar,
.menu-bar::before,
.menu-bar::after {
  display: block;
  width: 1.375rem;
  height: 2px;
  background: currentColor;
  border-radius: 9999px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.menu-bar {
  position: relative;
}

.menu-bar::before,
.menu-bar::after {
  content: '';
  position: absolute;
  left: 0;
}

.menu-bar::before {
  top: -6px;
}

.menu-bar::after {
  top: 6px;
}

.menu-bar.open {
  background: transparent;
}

.menu-bar.open::before {
  top: 0;
  transform: rotate(45deg);
}

.menu-bar.open::after {
  top: 0;
  transform: rotate(-45deg);
}

/* Mobile full-screen menu */
.mobile-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(3, 7, 18, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.mobile-nav {
  width: 100%;
  max-width: 20rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-link {
  display: block;
  padding: 1rem 0;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: border-color 0.25s ease;
}

.mobile-link:hover {
  border-bottom-color: rgba(45, 212, 191, 0.25);
}

.mobile-link-text {
  display: block;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #6b7280;
  transition: color 0.25s ease;
}

.mobile-link:hover .mobile-link-text {
  color: #e5e7eb;
}

.mobile-link.active .mobile-link-text {
  color: #fff;
  background: linear-gradient(90deg, #fff 0%, #99f6e4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.mobile-link-tag {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2dd4bf;
}

.beta-tag {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.1rem 0.42rem;
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  line-height: 1.4;
  color: #5eead4;
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.4);
  border-radius: 9999px;
  vertical-align: middle;
  transform: translateY(-1px);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-active .mobile-link,
.overlay-leave-active .mobile-link {
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-from .mobile-link {
  opacity: 0;
  transform: translateY(12px);
}

@media (prefers-reduced-motion: reduce) {
  .beam-active,
  .beam-preview {
    box-shadow: none;
  }

  .nav-link,
  .brand-text,
  .mobile-link-text,
  .menu-bar,
  .menu-bar::before,
  .menu-bar::after {
    transition: none;
  }
}
</style>
